import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import {
  TuiButton,
  TuiDialogService,
  TuiInput,
  TuiLoader,
  TuiNotification,
  TuiTextfield,
} from '@taiga-ui/core';
import { TuiTablePagination, TuiTablePaginationEvent } from '@taiga-ui/addon-table';
import { TUI_CONFIRM, TuiConfirmData, TuiToastService } from '@taiga-ui/kit';
import { EMPTY, switchMap } from 'rxjs';
import { TagStore } from '@domains/tag/tag.store';
import { ITag } from '@domains/tag/tag.model';

@Component({
  selector: 'app-settings-tags',
  templateUrl: 'tags.html',
  styleUrl: 'tags.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TuiAutoFocus,
    TuiButton,
    TuiInput,
    TuiLoader,
    TuiNotification,
    TuiTextfield,
    TuiTablePagination,
  ],
})
export class TagsComponent {
  private readonly store = inject(TagStore);
  private readonly dialogs = inject(TuiDialogService);
  private readonly toasts = inject(TuiToastService);

  protected readonly tags = this.store.tags;
  protected readonly total = this.store.total;
  protected readonly isLoading = this.store.isLoading;
  protected readonly error = this.store.error;
  protected readonly page = this.store.page;
  protected readonly pageSize = this.store.pageSize;

  /** The tag id currently being edited inline, or null. */
  protected readonly editingId = signal<number | null>(null);
  /** Whether a draft "new tag" row is shown at the top of the list. */
  protected readonly isAdding = signal(false);
  protected readonly saving = signal(false);

  protected readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  private readonly nameValue = toSignal(this.nameControl.valueChanges, { initialValue: '' });

  /** A row is being added or edited — disables the other actions to keep one edit at a time. */
  protected readonly isBusy = computed(() => this.isAdding() || this.editingId() !== null);
  protected readonly canSave = computed(() => this.nameValue().trim().length > 0 && !this.saving());

  protected onPagination({ page }: TuiTablePaginationEvent): void {
    this.store.setPage(page);
  }

  public startAddFromHeader(): void {
    if (this.isBusy()) return;

    this.editingId.set(null);
    this.isAdding.set(true);
    this.nameControl.reset('');
  }

  protected startEdit(tag: ITag): void {
    this.isAdding.set(false);
    this.editingId.set(tag.id);
    this.nameControl.setValue(tag.name);
  }

  protected cancel(): void {
    this.isAdding.set(false);
    this.editingId.set(null);
    this.nameControl.reset('');
  }

  protected saveNew(): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.store.createTag(this.nameControl.value.trim()).subscribe({
      next: () => {
        this.toasts.open('Tag added.', { appearance: 'positive' }).subscribe();
        this.saving.set(false);
        this.cancel();
      },
      error: () => {
        this.toasts.open('Failed to add tag.', { appearance: 'negative' }).subscribe();
        this.saving.set(false);
      },
    });
  }

  protected saveEdit(tag: ITag): void {
    if (!this.canSave()) return;
    this.saving.set(true);
    this.store.updateTag(tag, this.nameControl.value.trim()).subscribe({
      next: () => {
        this.toasts.open('Tag updated.', { appearance: 'positive' }).subscribe();
        this.saving.set(false);
        this.cancel();
      },
      error: () => {
        this.toasts.open('Failed to update tag.', { appearance: 'negative' }).subscribe();
        this.saving.set(false);
      },
    });
  }

  protected onDelete(tag: ITag): void {
    const data: TuiConfirmData = {
      content: `Do you want to delete the tag "${tag.name}"?`,
      yes: 'Delete',
      no: 'Cancel',
      appearance: 'primary-destructive',
    };

    this.dialogs
      .open<boolean>(TUI_CONFIRM, { label: 'Delete tag', size: 's', data })
      .pipe(switchMap((confirmed) => (confirmed ? this.store.deleteTag(tag.id) : EMPTY)))
      .subscribe({
        next: () => this.toasts.open('Tag deleted.', { appearance: 'positive' }).subscribe(),
        error: () =>
          this.toasts.open('Failed to delete tag.', { appearance: 'negative' }).subscribe(),
      });
  }
}
